import * as Sentry from "@sentry/react";
import { containerLog, CONTAINER_LOG } from "./containerLog";

// Bundle-side half of the boot sentinel (src/bootSentinel.js, inlined into the
// HTML by vite.config.js). The sentinel runs before this bundle and decides
// "boot failed" from the stage it was last told about; this module is what
// tells it. It also covers the whitescreens that happen *after* a successful
// mount without throwing — a route that renders nothing, a loader that never
// goes away — which no error boundary or global handler can observe.

const PENDING_KEY = "mrgBootFailures";
// A mounted app should have painted something within this long; the boot
// splash carries an <img> and text, so even the slowest first screen passes.
const BLANK_CHECK_MS = 8000;
// The full-screen <Loader /> is the only legitimate "no content" state, and it
// should never be the only thing on screen for this long.
const STUCK_LOADER_MS = 30000;

const sentinel = () => window.__mrgBootSentinel;

// Stages: "entry" (every module evaluated), "mounted" (first React commit),
// "error-fallback" (the error boundary took over — the crash itself is already
// in Sentry, so the sentinel must stay quiet).
export const markBootStage = (stage) => {
    try {
        sentinel()?.markBootStage(stage);
    } catch {
        /* sentinel absent (test harness, hand-written HTML) — nothing to mark */
    }
};

// Replays boot failures the sentinel could not deliver at the time (offline,
// or the container tore the page down mid-request). Runs on every boot that
// gets this far; an empty queue is the normal case. Tagged so a replayed event
// is distinguishable from a live one and — since the failing bundle may have
// been a different one — carries the release it failed under separately from
// the release that is reporting it.
export const flushPendingBootFailures = () => {
    let reports;
    try {
        const raw = localStorage.getItem(PENDING_KEY);
        if (!raw) return;
        localStorage.removeItem(PENDING_KEY);
        reports = JSON.parse(raw);
    } catch {
        return;
    }
    if (!Array.isArray(reports)) return;

    for (const report of reports) {
        if (!report || typeof report !== "object") continue;
        Sentry.withScope((scope) => {
            scope.setLevel("fatal");
            scope.setFingerprint(["boot-failed", String(report.kind)]);
            scope.setTags({
                boot_failure: String(report.kind),
                boot_stage: report.stage ?? "none",
                boot_report: "replayed-from-queue",
                failed_release: report.release ?? "unknown",
                install_id: report.installId ?? "unknown",
                route: report.route ?? "unknown",
            });
            scope.setContext("boot_failure", report);
            if (report.userId) scope.setUser({ id: String(report.userId) });
            const detail = report.firstError?.message ? `: ${report.firstError.message}` : "";
            Sentry.captureMessage(
                `Boot failed (${report.kind})${detail} — replayed from a previous launch`,
                "fatal"
            );
        });
    }
};

const activeRoute = () => {
    try {
        const hash = window.location.hash || "";
        const route = hash.startsWith("#/") ? hash.slice(1) : window.location.pathname;
        return (route || "unknown").split("?")[0];
    } catch {
        return "unknown";
    }
};

// "Content" is anything a person would recognise as a screen: text, or a
// media/form element. The <Loader /> spinner is an <svg> inside role="status",
// which the stuck-loader check deliberately excludes.
const CONTENT_SELECTOR = "img, video, canvas, svg, input, textarea, select, iframe";
const hasVisibleContent = (root, { ignoreLoader }) => {
    if ((root.innerText || "").trim()) return true;
    for (const node of root.querySelectorAll(CONTENT_SELECTOR)) {
        if (ignoreLoader && node.closest('[role="status"]')) continue;
        return true;
    }
    return false;
};

const reportRenderCheck = (kind, message) => {
    const route = activeRoute();
    const root = document.getElementById("root");
    containerLog(`Render check failed - kind=${kind} route=${route}`, CONTAINER_LOG.WARN);
    Sentry.withScope((scope) => {
        scope.setFingerprint([kind]);
        scope.setTags({ render_check: kind, route });
        scope.setContext("render_check", {
            route,
            rootChildren: root ? root.childElementCount : -1,
            rootHtmlLength: root ? root.innerHTML.length : -1,
            visibility: document.visibilityState,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
        });
        Sentry.captureMessage(message, "warning");
    });
};

// Called once, from the first React commit. Both checks are one-shot: the
// point is to learn that a class of silent blank exists on the fleet, not to
// measure its duration.
export const startRenderChecks = () => {
    setTimeout(() => {
        const root = document.getElementById("root");
        if (root && !hasVisibleContent(root, { ignoreLoader: false })) {
            reportRenderCheck("blank-render", "Blank render — app mounted but #root shows nothing");
        }
    }, BLANK_CHECK_MS);
    setTimeout(() => {
        const root = document.getElementById("root");
        if (root && !hasVisibleContent(root, { ignoreLoader: true })) {
            reportRenderCheck(
                "stuck-loader",
                "Stuck loader — only the loader is on screen 30s after mount"
            );
        }
    }, STUCK_LOADER_MS);
};

export const markMounted = () => {
    const wasReported = Boolean(sentinel()?.state?.reported);
    markBootStage("mounted");
    // The sentinel fired, then the app came up anyway: a slow boot, not a dead
    // one. Recorded so the fatal it already sent can be read in context.
    if (wasReported) {
        Sentry.captureMessage(
            "Boot completed after the sentinel had already reported it failed",
            "info"
        );
    }
    startRenderChecks();
};
